/**
 * Turns the wizard decisions into the backend `ContractImportCommitRequest` JSON and
 * validates the FE-checkable invariants beforehand (mirrors the BE `Validate` guard so
 * the user fixes issues in the summary instead of bouncing off a 400).
 */

import { ruleSpecToRequest } from './mapExtraction';
import { isBillingRuleComplete, needsReview } from '@/utils/billingKind';
import type {
  CommitPayload,
  ContactDecision,
  CustomerDecision,
  ObjectDecision,
  OfferDecision,
  PositionDecision,
} from './types';

const nullable = (value: string): string | null => (value.trim() ? value.trim() : null);

export interface CommitInput {
  customer: CustomerDecision;
  contact: ContactDecision;
  object: ObjectDecision;
  positions: PositionDecision[];
  offer: OfferDecision;
}

export function buildCommitPayload(input: CommitInput): CommitPayload {
  const { customer, contact, object, positions, offer } = input;

  return {
    customer:
      customer.mode === 'link'
        ? { existingCustomerId: customer.existingCustomerId }
        : {
            name: customer.name.trim(),
            street: nullable(customer.street),
            zip: nullable(customer.zip),
            city: nullable(customer.city),
            country: nullable(customer.country),
            vatId: nullable(customer.vatId),
          },
    contact: contact.enabled
      ? {
          firstName: contact.firstName.trim(),
          lastName: contact.lastName.trim(),
          role: nullable(contact.role),
          email: nullable(contact.email),
          phone: nullable(contact.phone),
        }
      : null,
    object:
      object.mode === 'link'
        ? { existingObjectId: object.existingObjectId }
        : {
            name: object.name.trim(),
            street: nullable(object.street),
            zip: nullable(object.zip),
            city: nullable(object.city),
            country: nullable(object.country),
          },
    positions: positions
      .filter((p) => p.include)
      .map((p) => ({
        service:
          p.service.mode === 'link'
            ? { existingServiceId: p.service.existingServiceId }
            : {
                name: p.service.name.trim(),
                category: nullable(p.service.category),
                unit: null,
                basePrice: null,
              },
        taskName: nullable(p.taskName),
        durationMinutes: p.durationMinutes,
        scheduleRules: p.scheduleRules.map(ruleSpecToRequest),
        checklistItems: p.checklistItems
          .filter((c) => c.title.trim())
          .map((c) => ({ title: c.title.trim(), intervalHint: nullable(c.intervalHint) })),
        offerTitle: p.offerTitle.trim(),
        intervalText: p.intervalText.trim(),
        netAmount: p.netAmount,
        recurrenceKind: p.recurrenceKind,
        billingRule: needsReview(p.recurrenceKind) ? null : p.billingRule,
      })),
    offer: {
      offerDate: offer.offerDate,
      taxRate: offer.taxRate,
      contactPersonName: nullable(offer.contactPersonName),
      introductionText: nullable(offer.introductionText),
      closingText: nullable(offer.closingText),
      paymentTerms: nullable(offer.paymentTerms),
    },
  };
}

/** One FE-detectable blocker; `key` is a `contractImport:summary.issues.*` i18n key. */
export interface CommitIssue {
  key: string;
  params?: Record<string, string | number>;
}

/** Mirrors the BE commit validation for everything the FE can check locally. */
export function validateCommitInput(input: CommitInput): CommitIssue[] {
  const issues: CommitIssue[] = [];
  const { customer, contact, object, positions } = input;
  const included = positions.filter((p) => p.include);

  if (included.length === 0) issues.push({ key: 'noPositions' });

  if (customer.mode === 'link' && !customer.existingCustomerId) issues.push({ key: 'linkCustomer' });
  if (customer.mode === 'create' && !customer.name.trim()) issues.push({ key: 'customerName' });

  if (contact.enabled && (!contact.firstName.trim() || !contact.lastName.trim())) {
    issues.push({ key: 'contactName' });
  }

  if (object.mode === 'link' && !object.existingObjectId) issues.push({ key: 'linkObject' });
  if (object.mode === 'create' && !object.name.trim()) issues.push({ key: 'objectName' });

  if (customer.mode === 'create' && customer.name.trim().length > 200) {
    issues.push({ key: 'tooLongCustomerName', params: { max: 200 } });
  }
  if (object.mode === 'create' && object.name.trim().length > 200) {
    issues.push({ key: 'tooLongObjectName', params: { max: 200 } });
  }

  for (const p of included) {
    const params = { number: p.positionNumber };
    if (p.service.mode === 'link' && !p.service.existingServiceId) issues.push({ key: 'linkService', params });
    if (p.service.mode === 'create' && !p.service.name.trim()) issues.push({ key: 'serviceName', params });
    if (!p.offerTitle.trim()) issues.push({ key: 'offerTitle', params });
    if (!p.intervalText.trim()) issues.push({ key: 'intervalText', params });
    if (!(p.durationMinutes >= 1 && p.durationMinutes <= 1440)) issues.push({ key: 'duration', params });
    if (p.netAmount < 0) issues.push({ key: 'netAmount', params });
    // why: the BE rejects e.g. half a season window (FAFD-27); "to review" may stay open.
    if (!needsReview(p.recurrenceKind) && !isBillingRuleComplete(p.recurrenceKind, p.billingRule)) {
      issues.push({ key: 'billingRule', params });
    }

    // Mirrors the BE column-length guards so overlong values surface here instead of a 400.
    const lengthChecks: Array<[string, number, string]> = [
      [p.service.mode === 'create' ? p.service.name : '', 200, 'tooLongServiceName'],
      [p.taskName, 200, 'tooLongTaskName'],
      [p.offerTitle, 200, 'tooLongOfferTitle'],
      [p.intervalText, 200, 'tooLongIntervalText'],
    ];
    for (const [value, max, key] of lengthChecks) {
      if (value.trim().length > max) {
        issues.push({ key, params: { ...params, max } });
      }
    }
    if (p.checklistItems.some((item) => item.title.trim().length > 300)) {
      issues.push({ key: 'tooLongChecklistTitle', params: { ...params, max: 300 } });
    }
    if (p.checklistItems.some((item) => item.intervalHint.trim().length > 100)) {
      issues.push({ key: 'tooLongChecklistHint', params: { ...params, max: 100 } });
    }
  }

  return issues;
}
